/**
 * Helpers for surfacing server-side Zod validation errors back to the user.
 *
 * profiles-api responds to validation failures with:
 *   { error: "Validation failed", code: "VALIDATION_ERROR", details: ZodIssue[] }
 *
 * `details` is the raw `error.issues` array from Zod, each entry shaped as:
 *   { path: (string | number)[]; message: string; code: string; ... }
 *
 * Unlike account-ui (which throws a structured `ApiError`), staff-ui's
 * `api/client.ts` throws a plain `Error` decorated with `.status`, `.code`,
 * `.details` (the parsed array when it is one) and `.detailsRaw` (the raw
 * `details` value, whatever its shape). This helper reaches into those fields
 * and translates them into a shape the forms already understand
 * (`Record<fieldKey, string>` for known fields plus a fallback list of
 * human-readable messages for issues whose path doesn't map to a known field).
 */

export type ZodIssueLike = {
  path: ReadonlyArray<string | number>;
  message: string;
  code?: string;
};

/**
 * Errors for repeatable list fields (rendered via the `Repeater` component).
 *
 * Keyed by the array field name (e.g. `"staffing"`). The value is an array
 * indexed by row position — entry `i` corresponds to row `i`. Each entry maps a
 * sub-field name to its message (e.g. `{ title: "Required" }`).
 *
 * The array is **sparse**: index 1 may be present while 0 is absent, mirroring
 * Zod which only reports issues for the rows that actually failed.
 *
 * Row-level / primitive-element errors (a failing path like `["languages", 0]`
 * with no trailing sub-field) are stored under the conventional empty-string
 * sub-key `""`, i.e. `arrayErrors.languages[0][""] = message`.
 */
export type ArrayFieldErrors = Record<string, Array<Record<string, string> | undefined>>;

export type ValidationErrorMap<TKey extends string> = {
  /** Field-level errors keyed by the same shape the form uses for client-side validation. */
  fieldErrors: Partial<Record<TKey, string>>;
  /** Per-row errors for repeatable array fields (see `ArrayFieldErrors`). */
  arrayErrors: ArrayFieldErrors;
  /** Human-readable messages for issues that didn't map to a known field. */
  orphanMessages: string[];
};

type ApiErrorLike = Error & {
  status?: number;
  code?: string;
  details?: unknown;
  detailsRaw?: unknown;
};

function isZodIssueArray(value: unknown): value is ZodIssueLike[] {
  if (!Array.isArray(value)) return false;
  return value.every(
    entry =>
      entry !== null &&
      typeof entry === "object" &&
      "path" in entry &&
      Array.isArray((entry as { path: unknown }).path) &&
      "message" in entry &&
      typeof (entry as { message: unknown }).message === "string",
  );
}

/**
 * Pull the Zod issue array out of an unknown error if it looks like a
 * 400 validation error from profiles-api. Returns null otherwise.
 *
 * staff-ui's `api/client.ts` parks the parsed `details` field on the thrown
 * Error as `.details` (when it parsed to an array) and the raw value on
 * `.detailsRaw`. We accept either: a top-level Zod issue array, or a
 * `{ details: ZodIssue[] }` envelope nested under `detailsRaw`.
 */
export function extractZodIssues(error: unknown): ZodIssueLike[] | null {
  if (!(error instanceof Error)) return null;
  const apiError = error as ApiErrorLike;
  if (apiError.status !== 400) return null;

  // Fast path: client.ts already parsed an array `details` onto `.details`.
  if (isZodIssueArray(apiError.details)) return apiError.details;

  // Fallback: inspect the raw `details` value, which may be the array itself
  // or a `{ details: ZodIssue[] }` envelope.
  const raw = apiError.detailsRaw;
  if (isZodIssueArray(raw)) return raw;
  if (raw && typeof raw === "object" && "details" in raw) {
    const inner = (raw as { details?: unknown }).details;
    if (isZodIssueArray(inner)) return inner;
  }
  return null;
}

/**
 * Translate a list of Zod issues into a field-error map for the supplied
 * known field keys, plus a list of fallback messages for orphan issues
 * (those whose path doesn't resolve to a known key).
 *
 * - Path is joined with `.` to make a stable key (e.g. `["bio", "value"]` →
 *   `"bio.value"`); we also try the first path segment alone so that nested
 *   issues on rich-text fields still attach to the parent field.
 * - The first issue per key wins, mirroring client-side behaviour.
 *
 * Nested array fields (the `arrayKeys` argument, e.g. `["staffing"]`) are
 * routed into `arrayErrors` instead:
 *   - `[arrayKey, <number index>, <subfield string>]` →
 *     `arrayErrors[arrayKey][index][subfield] = message`
 *   - `[arrayKey, <number index>]` (no sub-field, e.g. a primitive element like
 *     `languages.0`) → stored under the empty-string sub-key:
 *     `arrayErrors[arrayKey][index][""] = message`
 *   - First write per `[index][subfield]` wins.
 *   - Any other shape under an `arrayKey` (e.g. a deeper path, or a non-numeric
 *     second segment) falls through to the flat/orphan handling below.
 *
 * When `arrayKeys` is undefined or empty the behaviour is identical — `arrayErrors`
 * is simply an empty object.
 */
export function mapZodIssuesToFieldErrors<TKey extends string>(
  issues: ReadonlyArray<ZodIssueLike>,
  knownKeys: ReadonlyArray<TKey>,
  arrayKeys?: ReadonlyArray<string>,
): ValidationErrorMap<TKey> {
  const known = new Set<string>(knownKeys);
  const arrayKeySet = new Set<string>(arrayKeys ?? []);
  const fieldErrors: Partial<Record<TKey, string>> = {};
  const arrayErrors: ArrayFieldErrors = {};
  const orphanMessages: string[] = [];

  for (const issue of issues) {
    const path = issue.path ?? [];
    const head = path.length > 0 ? String(path[0]) : "";
    const full = path.map(String).join(".");

    // Nested array-field routing: [arrayKey, index] or [arrayKey, index, sub].
    if (
      head &&
      arrayKeySet.has(head) &&
      path.length >= 2 &&
      path.length <= 3 &&
      typeof path[1] === "number"
    ) {
      const index = path[1] as number;
      const sub = path.length === 3 ? String(path[2]) : "";
      const rows = arrayErrors[head] ?? (arrayErrors[head] = []);
      const row = rows[index] ?? (rows[index] = {});
      if (!(sub in row)) {
        row[sub] = issue.message;
      }
      continue;
    }

    let matchedKey: TKey | null = null;
    if (full && known.has(full)) {
      matchedKey = full as TKey;
    } else if (head && known.has(head)) {
      matchedKey = head as TKey;
    }

    if (matchedKey) {
      if (!fieldErrors[matchedKey]) {
        fieldErrors[matchedKey] = issue.message;
      }
      continue;
    }

    const label = full || "(unknown field)";
    orphanMessages.push(`${label}: ${issue.message}`);
  }

  return { fieldErrors, arrayErrors, orphanMessages };
}
